import React, { useState, useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { usePermission } from 'hooks/usePermission';

/**
 * ProtectedRoute
 *
 * Gates any route by department, roles, and/or permissions.
 *
 * ─── Props ────────────────────────────────────────────────────────────────────
 *
 *  departments  string[]  – user's department.code must be in this list
 *                           e.g. ['QS', 'AC'] or leave empty to skip dept check
 *
 *  roles        string[]  – user must have AT LEAST ONE of these roles
 *
 *  permissions  string[]  – user must have AT LEAST ONE of these permissions
 *
 *  requireAll   boolean   – when true, ALL roles AND ALL permissions must pass
 *                           (departments check is always ANY-match)
 *
 *  redirect     string    – where to send on failure if useModal is false
 *                           (default: '/unauthorized')
 *
 *  useModal     boolean   – when true (default), show the UnauthorizedModal
 *                           and navigate back instead of hard-redirecting
 *
 * ─── Examples ─────────────────────────────────────────────────────────────────
 *
 *   // Only QS or admin
 *   <ProtectedRoute departments={['QS']}>
 *
 *   // Any admin role
 *   <ProtectedRoute roles={['super_admin', 'system_admin']}>
 *
 *   // Department + permission
 *   <ProtectedRoute departments={['AC']} permissions={['financial_records.read']}>
 *
 *   // Hard-redirect instead of modal
 *   <ProtectedRoute departments={['PR']} useModal={false} redirect="/403">
 */
const ProtectedRoute = ({
    children,
    departments = [],   // department codes e.g. ['QS', 'AC']
    roles = [],
    permissions = [],
    requireAll = false,
    redirect = '/unauthorized/action',
}) => {
    const { user, loading, isAdmin } = useAuth();
    const { canAny, canAll, hasRole, hasAllRoles } = usePermission();
    const location = useLocation();

    // ── Still bootstrapping ────────────────────────────────────────────────────
    if (loading) return null;

    // ── Not logged in ──────────────────────────────────────────────────────────
    if (!user) {
        if (location.pathname === '/pages/login/login3') return null;
        return <Navigate to="/pages/login/login3" state={{ from: location }} replace />;
    }

    // ── Admins bypass every gate ───────────────────────────────────────────────
    if (isAdmin) return children;

    // ─────────────────────────────────────────────────────────────────────────
    // EVALUATE GATES
    // ─────────────────────────────────────────────────────────────────────────

    const userDeptCode = user?.department?.code ?? null;

    // 1. Department gate
    let deptPass = true;
    if (departments.length > 0) {
        // Admins already returned above; department_head can access their own dept
        deptPass = userDeptCode ? departments.includes(userDeptCode) : false;
    }

    // 2. Role gate
    let rolePass = true;
    if (roles.length > 0) {
        rolePass = requireAll ? hasAllRoles(roles) : hasRole(roles);
    }

    // 3. Permission gate
    let permPass = true;
    if (permissions.length > 0) {
        permPass = requireAll ? canAll(permissions) : canAny(permissions);
    }

    // 4. Combine
    //    Rule: every specified gate must independently pass.
    //    If requireAll=true, roles AND permissions must ALL pass.
    //    Departments are always ANY-match (included in deptPass already).
    let granted;
    if (requireAll) {
        granted = deptPass && rolePass && permPass;
    } else {
        // Only check gates that were actually configured
        const checks = [];
        if (departments.length > 0) checks.push(deptPass);
        if (roles.length > 0) checks.push(rolePass);
        if (permissions.length > 0) checks.push(permPass);

        // Nothing configured → open route
        if (checks.length === 0) granted = true;
        // Otherwise ANY gate passing is enough (loose mode)
        else granted = checks.some(Boolean);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLE DENIAL
    // ─────────────────────────────────────────────────────────────────────────
    if (!granted) {
        // Build a human-readable reason for the modal
        const buildReason = () => {
            if (departments.length > 0 && !deptPass) {
                return `This section is restricted to the ${departments.join(' or ')} department(s). Your current department does not have access.`;
            }
            if (roles.length > 0 && !rolePass) {
                return `Your role does not have access to this section.`;
            }
            if (permissions.length > 0 && !permPass) {
                return `You don't have the required permissions to view this page.`;
            }
            return "You don't have permission to access this page.";
        };

        // Hard-redirect fallback (when useModal=false)
        if (location.pathname === redirect) return null;
        return <Navigate to={redirect} state={{ from: location }} replace />;
    }

    return children;
};

export default ProtectedRoute;